import { reactive, ref, watch } from "vue";

export interface ClassContact {
  name?: string;
  email?: string;
}

export interface HasClassContacts {
  presentedByObj?: ClassContact;
  coHostedByObj?: ClassContact;
}

export function serializeContact(contact?: ClassContact) {
  if (!contact?.name && !contact?.email) return null;
  return JSON.stringify({ email: contact.email ?? "", name: contact.name ?? "" });
}

/** Shared presenter / co-host wiring for the class edit forms that use
 * `PresentedByAutocomplete` and `CoHostedByAutocomplete`. */
export function usePresenterContacts() {
  const presentedByRef = ref<ClassContact>({});
  const coHostedByRef = ref<ClassContact>({});

  // The autocompletes read these imperatively while FormKit initializes, which happens before the
  // next render flush, so the object identity has to stay stable and the values have to be current.
  const presenterEntity = reactive<{ presentedBy: string | null; presentedByEmail: string | null }>({
    presentedBy: null,
    presentedByEmail: null,
  });
  const coHostEntity = reactive<{ coHostedBy: string | null; coHostedByEmail: string | null }>({
    coHostedBy: null,
    coHostedByEmail: null,
  });

  watch(
    [presentedByRef, coHostedByRef],
    () => {
      presenterEntity.presentedBy = serializeContact(presentedByRef.value);
      presenterEntity.presentedByEmail = presentedByRef.value?.email ?? null;
      coHostEntity.coHostedBy = serializeContact(coHostedByRef.value);
      coHostEntity.coHostedByEmail = coHostedByRef.value?.email ?? null;
    },
    { flush: "sync", immediate: true },
  );

  const setContacts = (data?: HasClassContacts) => {
    presentedByRef.value = data?.presentedByObj ?? {};
    coHostedByRef.value = data?.coHostedByObj ?? {};
  };

  const applyContacts = <T extends HasClassContacts>(data: T) => {
    data.presentedByObj = presentedByRef.value;
    data.coHostedByObj = coHostedByRef.value;
    return data;
  };

  return {
    presentedByRef,
    coHostedByRef,
    presenterEntity,
    coHostEntity,
    setContacts,
    applyContacts,
  };
}
